import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart3,
  Building2,
  ChevronRight,
  Edit,
  FileText,
  FolderKanban,
  LogOut,
  MapPin,
  Plus,
  Trash2,
  Trophy,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";

const GRADE_VALUES = { A: 4, B: 3, C: 2, D: 1 };
const GRADE_COLORS = {
  A: { bg: "bg-emerald-100", text: "text-emerald-700" },
  B: { bg: "bg-blue-100", text: "text-blue-700" },
  C: { bg: "bg-amber-100", text: "text-amber-700" },
  D: { bg: "bg-orange-100", text: "text-orange-700" },
};

const createInitialStudentState = (context = {}) => ({
  name: "",
  dob: "",
  parent_email: "",
  skill_level: "Beginner",
  center_id: context.center_id || "",
  batch_id: context.batch_id || "",
});

const calculateAgePreview = (dob) => {
  if (!dob) {
    return null;
  }

  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return Math.max(age, 0);
};

export default function CoachDashboard() {
  const navigate = useNavigate();
  const { centerId, batchId } = useParams();

  const [centers, setCenters] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [quarterlyReports, setQuarterlyReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [newStudent, setNewStudent] = useState(createInitialStudentState());
  const [formBatches, setFormBatches] = useState([]);

  const userName = localStorage.getItem("userName");
  const scope = batchId ? "batch" : centerId ? "center" : "academy";
  const currentCenter = centers.find((center) => center.id === centerId) || null;
  const currentBatch = batches.find((batch) => batch.id === batchId) || null;

  const buildStudentParams = () => {
    if (scope === "batch") {
      return { center_id: centerId, batch_id: batchId };
    }

    if (scope === "center") {
      return { center_id: centerId };
    }

    return {};
  };

  const fetchBatchesForCenter = useCallback(async (selectedCenterId) => {
    if (!selectedCenterId) {
      setFormBatches([]);
      return [];
    }

    const response = await api.get(`/centers/${selectedCenterId}/batches`);
    setFormBatches(response.data);
    return response.data;
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);

    try {
      const centersResponse = await api.get("/centers");
      setCenters(centersResponse.data);

      let scopedBatches = [];
      if (centerId) {
        const batchesResponse = await api.get(`/centers/${centerId}/batches`);
        scopedBatches = batchesResponse.data;
      }
      setBatches(scopedBatches);

      const studentsResponse = await api.get("/students", { params: buildStudentParams() });
      setStudents(studentsResponse.data);

      const reportsPromises = studentsResponse.data.map((student) =>
        api.get(`/reports/quarterly/${student.id}`).catch(() => ({ data: [] }))
      );
      const reportsResults = await Promise.all(reportsPromises);
      const reportsMap = {};
      studentsResponse.data.forEach((student, index) => {
        reportsMap[student.id] = reportsResults[index].data;
      });
      setQuarterlyReports(reportsMap);

      if (scope === "academy") {
        setFormBatches([]);
      } else if (scope === "center") {
        setFormBatches(scopedBatches);
      } else {
        setFormBatches(scopedBatches);
      }
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [batchId, centerId, fetchBatchesForCenter, scope]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const openCreateStudentDialog = async (open) => {
    setShowAddStudent(open);
    if (!open) {
      return;
    }

    const initialState = createInitialStudentState({
      center_id: centerId || "",
      batch_id: batchId || "",
    });
    setNewStudent(initialState);

    if (initialState.center_id) {
      try {
        await fetchBatchesForCenter(initialState.center_id);
      } catch (error) {
        toast.error("Failed to load batches for player assignment");
      }
    } else {
      setFormBatches([]);
    }
  };

  const handleCreateCenterChange = async (selectedCenterId) => {
    const nextStudent = {
      ...newStudent,
      center_id: selectedCenterId,
      batch_id: "",
    };
    setNewStudent(nextStudent);

    try {
      await fetchBatchesForCenter(selectedCenterId);
    } catch (error) {
      toast.error("Failed to load batches for selected center");
    }
  };

  const handleAddStudent = async (event) => {
    event.preventDefault();

    try {
      await api.post("/students", { ...newStudent });
      toast.success("Player added successfully");
      setShowAddStudent(false);
      setNewStudent(
        createInitialStudentState({
          center_id: centerId || "",
          batch_id: batchId || "",
        })
      );
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add player");
    }
  };

  const handleEditStudent = async (event) => {
    event.preventDefault();

    try {
      await api.put(`/students/${editingStudent.id}`, {
        name: editingStudent.name,
        dob: editingStudent.dob,
        parent_email: editingStudent.parent_email,
        center_id: editingStudent.center_id || null,
        batch_id: editingStudent.batch_id || null,
        skill_level: editingStudent.skill_level,
      });
      toast.success("Player updated successfully");
      setEditingStudent(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update player");
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete ${studentName}? This will also delete all reports.`)) {
      return;
    }

    try {
      await api.delete(`/students/${studentId}`);
      toast.success("Player deleted successfully");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete player");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const getOverallGrade = (reports) => {
    if (!reports || reports.length === 0) {
      return null;
    }

    const latest = reports[0];
    const skills = latest.grades.performance;
    const average =
      (GRADE_VALUES[skills.passing] +
        GRADE_VALUES[skills.shooting] +
        GRADE_VALUES[skills.dribbling] +
        GRADE_VALUES[skills.heading] +
        GRADE_VALUES[skills.ball_control] +
        GRADE_VALUES[skills.match_play]) /
      6;

    if (average >= 3.5) {
      return "A";
    }
    if (average >= 2.5) {
      return "B";
    }
    if (average >= 1.5) {
      return "C";
    }
    return "D";
  };

  const getReportCountForStudents = (studentList) =>
    studentList.reduce((total, student) => total + (quarterlyReports[student.id] || []).length, 0);

  const centerCards = centers.map((center) => {
    const centerStudents = students.filter((student) => student.center_id === center.id);
    return {
      ...center,
      playerCount: centerStudents.length,
      reportCount: getReportCountForStudents(centerStudents),
      batchCount: new Set(centerStudents.map((student) => student.batch_id).filter(Boolean)).size,
    };
  });

  const batchCards = batches.map((batch) => {
    const batchStudents = students.filter((student) => student.batch_id === batch.id);
    return {
      ...batch,
      playerCount: batchStudents.length,
      reportCount: getReportCountForStudents(batchStudents),
    };
  });

  const totalReports = Object.values(quarterlyReports).reduce((accumulator, reports) => accumulator + reports.length, 0);
  const addStudentAge = calculateAgePreview(newStudent.dob);

  const getScopeLabel = () => {
    if (scope === "academy") {
      return "Academy Dashboard";
    }
    if (scope === "center") {
      return currentCenter?.name || "Center Dashboard";
    }
    return currentBatch?.name || "Batch Dashboard";
  };

  const renderBreadcrumbs = () => (
    <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
      <button
        className="hover:text-slate-800 transition-colors"
        onClick={() => navigate("/coach")}
        type="button"
      >
        Academy
      </button>
      {centerId && (
        <>
          <ChevronRight className="w-4 h-4" />
          <button
            className="hover:text-slate-800 transition-colors"
            onClick={() => navigate(`/coach/centers/${centerId}`)}
            type="button"
          >
            {currentCenter?.name || "Center"}
          </button>
        </>
      )}
      {batchId && (
        <>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-800 font-semibold">{currentBatch?.name || "Batch"}</span>
        </>
      )}
    </div>
  );

  const renderStudentContextFields = () => {
    if (scope === "academy") {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="student-center" className="text-sm font-semibold">Center</Label>
            <select
              id="student-center"
              className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white px-3"
              value={newStudent.center_id}
              onChange={(event) => handleCreateCenterChange(event.target.value)}
              required
            >
              <option value="">Select center</option>
              {centers.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="student-batch" className="text-sm font-semibold">Batch</Label>
            <select
              id="student-batch"
              className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white px-3"
              value={newStudent.batch_id}
              onChange={(event) => setNewStudent({ ...newStudent, batch_id: event.target.value })}
              required
              disabled={!newStudent.center_id}
            >
              <option value="">Select batch</option>
              {formBatches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }

    if (scope === "center") {
      return (
        <>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Center</Label>
            <div className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 px-4 flex items-center text-slate-700 font-medium">
              {currentCenter?.name || "Selected center"}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="student-batch" className="text-sm font-semibold">Batch</Label>
            <select
              id="student-batch"
              className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white px-3"
              value={newStudent.batch_id}
              onChange={(event) => setNewStudent({ ...newStudent, batch_id: event.target.value })}
              required
            >
              <option value="">Select batch</option>
              {formBatches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Center</Label>
          <div className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 px-4 flex items-center text-slate-700 font-medium">
            {currentCenter?.name || "Selected center"}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Batch</Label>
          <div className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 px-4 flex items-center text-slate-700 font-medium">
            {currentBatch?.name || "Selected batch"}
          </div>
        </div>
      </>
    );
  };

  const renderScopeContent = () => {
    if (loading) {
      return <div className="text-center py-16 text-slate-600">Loading...</div>;
    }

    if (scope === "academy") {
      return (
        <Card className="shadow-xl border-0 rounded-3xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="border-b border-slate-100 pb-6">
            <CardTitle className="text-2xl font-bold">Centers</CardTitle>
            <CardDescription>Browse centers to drill into their batches and player activity.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {centerCards.length === 0 ? (
              <div className="text-center py-16 text-slate-600">No centers available yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {centerCards.map((center) => (
                  <Card
                    key={center.id}
                    className="border-0 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer"
                    onClick={() => navigate(`/coach/centers/${center.id}`)}
                  >
                    <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-5 text-white rounded-t-2xl">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold">{center.name}</h3>
                          <p className="text-sm text-slate-300 flex items-center gap-1 mt-1">
                            <MapPin className="w-4 h-4" />
                            {center.location || "Location not set"}
                          </p>
                        </div>
                        <Building2 className="w-10 h-10 text-emerald-300" />
                      </div>
                    </div>
                    <CardContent className="p-5 space-y-4">
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-2xl font-bold text-slate-900">{center.playerCount}</div>
                          <div className="text-xs text-slate-500">Players</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-2xl font-bold text-slate-900">{center.reportCount}</div>
                          <div className="text-xs text-slate-500">Reports</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-2xl font-bold text-slate-900">{center.batchCount}</div>
                          <div className="text-xs text-slate-500">Batches</div>
                        </div>
                      </div>
                      <Button className="w-full rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-900 hover:to-black">
                        Open Center
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (scope === "center") {
      return (
        <Card className="shadow-xl border-0 rounded-3xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="border-b border-slate-100 pb-6">
            <CardTitle className="text-2xl font-bold">Batches</CardTitle>
            <CardDescription>Choose a batch to view student cards and existing report activity.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {batchCards.length === 0 ? (
              <div className="text-center py-16 text-slate-600">No batches available in this center yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {batchCards.map((batch) => (
                  <Card
                    key={batch.id}
                    className="border-0 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer"
                    onClick={() => navigate(`/coach/centers/${centerId}/batches/${batch.id}`)}
                  >
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white rounded-t-2xl">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold">{batch.name}</h3>
                          <p className="text-sm text-emerald-100 mt-1">{currentCenter?.name || "Center"}</p>
                        </div>
                        <FolderKanban className="w-10 h-10 text-white/80" />
                      </div>
                    </div>
                    <CardContent className="p-5 space-y-4">
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-2xl font-bold text-slate-900">{batch.playerCount}</div>
                          <div className="text-xs text-slate-500">Players</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-2xl font-bold text-slate-900">{batch.reportCount}</div>
                          <div className="text-xs text-slate-500">Reports</div>
                        </div>
                      </div>
                      <Button className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                        Open Batch
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="shadow-xl border-0 rounded-3xl bg-white/80 backdrop-blur-sm">
        <CardHeader className="border-b border-slate-100 pb-6">
          <CardTitle className="text-2xl font-bold">Students</CardTitle>
          <CardDescription>View detailed player cards, reports, and quick actions for this batch.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {students.length === 0 ? (
            <div className="text-center py-16" data-testid="no-students-message">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl flex items-center justify-center mb-4">
                <Users className="w-10 h-10 text-slate-400" />
              </div>
              <p className="text-lg text-slate-600">No players yet in this batch. Add your first player to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {students.map((student) => {
                const reports = quarterlyReports[student.id] || [];
                const overallGrade = getOverallGrade(reports);
                const latestReport = reports[0];

                return (
                  <Card
                    key={student.id}
                    className="shadow-lg border-0 rounded-2xl bg-white hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    data-testid={`student-card-${student.id}`}
                  >
                    <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-emerald-500 rounded-xl flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg" data-testid={`student-name-${student.id}`}>{student.name}</h3>
                            <p className="text-slate-400 text-sm">Age: {student.age}</p>
                            <p className="text-slate-400 text-xs">{student.center_name} • {student.batch_name}</p>
                          </div>
                        </div>
                        {overallGrade && (
                          <div className={`w-12 h-12 ${GRADE_COLORS[overallGrade].bg} rounded-xl flex items-center justify-center`}>
                            <span className={`text-xl font-bold ${GRADE_COLORS[overallGrade].text}`}>{overallGrade}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{student.skill_level}</span>
                        <span className="text-slate-500">{reports.length} report{reports.length !== 1 ? "s" : ""}</span>
                      </div>

                      {latestReport && (
                        <div className="bg-emerald-50 rounded-xl p-3">
                          <p className="text-xs text-emerald-600 font-medium mb-1">Latest: {latestReport.quarter} {latestReport.year}</p>
                          <div className="flex gap-1">
                            {Object.values(latestReport.grades.performance).map((grade, index) => (
                              <span
                                key={index}
                                className={`w-6 h-6 text-xs flex items-center justify-center rounded ${GRADE_COLORS[grade].bg} ${GRADE_COLORS[grade].text} font-bold`}
                              >
                                {grade}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="h-10 font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl"
                          onClick={() => navigate(`/performance/add/${student.id}`)}
                          data-testid={`add-report-btn-${student.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Report
                        </Button>
                        <Button
                          variant="outline"
                          className="h-10 font-semibold border-2 rounded-xl"
                          onClick={() => navigate(`/student/${student.id}`)}
                          data-testid={`view-student-btn-${student.id}`}
                        >
                          View
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingStudent(student);
                          }}
                          data-testid={`edit-student-btn-${student.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteStudent(student.id, student.name);
                          }}
                          data-testid={`delete-student-btn-${student.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <nav className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/gfa-logo.png?v=5" alt="GFA Logo" className="w-10 h-10 object-contain rounded-full bg-white p-0.5" />
              <div>
                <h1 className="text-xl font-bold" data-testid="dashboard-title">German Football Academy</h1>
                <p className="text-sm text-slate-400">{getScopeLabel()}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-emerald-500/20 px-4 py-2 rounded-full border border-white/10">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-white/90">STADIA</span>
              </div>
              <span className="text-sm font-medium hidden md:block" data-testid="coach-name">{userName}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="hover:bg-white/10 text-white rounded-xl"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {renderBreadcrumbs()}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="shadow-lg border-0 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-200" />
                <CardDescription className="text-emerald-100 font-medium">Total Players</CardDescription>
              </div>
              <CardTitle className="text-5xl font-bold" data-testid="total-students">{students.length}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="shadow-lg border-0 rounded-2xl bg-white">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <CardDescription className="font-medium">Quarterly Reports</CardDescription>
              </div>
              <CardTitle className="text-5xl font-bold text-slate-900">{totalReports}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="shadow-lg border-0 rounded-2xl bg-white">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-500" />
                <CardDescription className="font-medium">Assessment Type</CardDescription>
              </div>
              <CardTitle className="text-3xl font-bold text-violet-600">Quarterly</CardTitle>
              <p className="text-sm text-slate-500">Comprehensive evaluations</p>
            </CardHeader>
          </Card>
        </div>

        <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{getScopeLabel()}</h2>
            <p className="text-slate-500">
              {scope === "academy"
                ? "Review center-level performance and create players with full placement details."
                : scope === "center"
                  ? "Review batches in this center and create players with the center preselected."
                  : "Manage students and reports for the active batch."}
            </p>
          </div>

          <Dialog open={showAddStudent} onOpenChange={openCreateStudentDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-lg shadow-emerald-500/30 rounded-xl h-12 px-6">
                <UserPlus className="w-5 h-5 mr-2" />
                Add New Player
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-[560px] rounded-2xl" data-testid="add-student-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Add New Player</DialogTitle>
                <DialogDescription>
                  {scope === "academy"
                    ? "Enter player details and choose both center and batch."
                    : scope === "center"
                      ? "Enter player details. This center is already selected."
                      : "Enter player details. This player will be added directly into the current batch."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddStudent} className="space-y-5 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="student-name" className="text-sm font-semibold">Player Name</Label>
                    <Input
                      id="student-name"
                      data-testid="student-name-input"
                      placeholder="Max Muller"
                      className="h-12 rounded-xl border-2"
                      value={newStudent.name}
                      onChange={(event) => setNewStudent({ ...newStudent, name: event.target.value })}
                      required
                    />
                  </div>

                  {renderStudentContextFields()}

                  <div className="space-y-2">
                    <Label htmlFor="student-dob" className="text-sm font-semibold">Date of Birth</Label>
                    <Input
                      id="student-dob"
                      data-testid="student-dob-input"
                      type="date"
                      className="h-12 rounded-xl border-2"
                      value={newStudent.dob}
                      onChange={(event) => setNewStudent({ ...newStudent, dob: event.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Age Preview</Label>
                    <div className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 px-4 flex items-center text-slate-700 font-medium">
                      {addStudentAge === null ? "Select date of birth" : `${addStudentAge} years`}
                    </div>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="parent-email" className="text-sm font-semibold">Parent Email (Optional)</Label>
                    <Input
                      id="parent-email"
                      data-testid="parent-email-input"
                      type="email"
                      placeholder="parent@email.com"
                      className="h-12 rounded-xl border-2"
                      value={newStudent.parent_email}
                      onChange={(event) => setNewStudent({ ...newStudent, parent_email: event.target.value })}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full h-12 font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl" data-testid="submit-student-btn">
                  Add Player
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {renderScopeContent()}

        <Dialog open={!!editingStudent} onOpenChange={() => setEditingStudent(null)}>
          <DialogContent className="sm:max-w-[500px] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Edit Player</DialogTitle>
              <DialogDescription>Update player information</DialogDescription>
            </DialogHeader>
            {editingStudent && (
              <form onSubmit={handleEditStudent} className="space-y-5 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-student-name" className="text-sm font-semibold">Player Name</Label>
                  <Input
                    id="edit-student-name"
                    className="h-12 rounded-xl border-2"
                    value={editingStudent.name}
                    onChange={(event) => setEditingStudent({ ...editingStudent, name: event.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-student-dob" className="text-sm font-semibold">Date of Birth</Label>
                  <Input
                    id="edit-student-dob"
                    type="date"
                    className="h-12 rounded-xl border-2"
                    value={editingStudent.dob || ""}
                    onChange={(event) => setEditingStudent({ ...editingStudent, dob: event.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-parent-email" className="text-sm font-semibold">Parent Email</Label>
                  <Input
                    id="edit-parent-email"
                    type="email"
                    className="h-12 rounded-xl border-2"
                    value={editingStudent.parent_email || ""}
                    onChange={(event) => setEditingStudent({ ...editingStudent, parent_email: event.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full h-12 font-semibold bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-900 hover:to-black rounded-xl">
                  Update Player
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}